/**
 * Четвёрка заказа: состав, сравнение, замена и выбор (BE-222, FE-220…FE-222).
 *
 * Три правила слоя:
 *
 * * **необратимые действия без оптимистичного UI** (CLAUDE.md §8). Замена
 *   освобождает слот, выбор запускает работу с автохаусом — рисовать
 *   результат до ответа сервера нельзя;
 * * **ключ идемпотентности живёт до успеха, а не до рендера.** Двойной клик и
 *   повтор после обрыва сети обязаны попасть в одно и то же действие;
 * * **ответ мутации кладётся в кэш, а не выбрасывается.** Он и есть новое
 *   состояние `GET /selection`; перезапрашивать состав после каждой кнопки —
 *   лишний round-trip и лишний шанс показать промежуточное состояние.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { MandateInput } from '@entities/negotiation';
import { apiFetch } from '@shared/api/client';
import { queryKeys } from '@shared/api/queryKeys';
import { randomUuid } from '@shared/lib/uuid';

import { BUILDING_STAGES } from './lib';
import type { ComparisonState, RejectReason, SelectionState } from './types';

/** Пока четвёрка ещё складывается, состав опрашивается раз в секунду. */
const TOP_UP_POLL_MS = 1_000;

function base(searchId: string): string {
  return `/search-sessions/${encodeURIComponent(searchId)}`;
}

export function useSelection(searchId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.selection(searchId ?? ''),
    queryFn: () => apiFetch<SelectionState>(`${base(searchId ?? '')}/selection`),
    enabled: enabled && searchId !== null && searchId !== '',
    retry: false,
    // Четвёрка ещё складывается — экран обновляется сам и не требует перезагрузки.
    //
    // Два случая, а не один. Освободился слот (`top_up_pending`) — Karl
    // подбирает замену. Перестроен подбор — заказ проходит `analyzing → paid →
    // selecting`, и всё это время слотов нет вовсе: `top_up_pending` тогда
    // равен `false`, потому что и кандидатов ещё нет. Без второго условия
    // экран замирал бы на «Karl собирает четвёрку» до перезагрузки страницы.
    refetchInterval: (query) => {
      const state = query.state.data;
      if (state === undefined) return false;
      if (state.top_up_pending) return TOP_UP_POLL_MS;
      return (BUILDING_STAGES as readonly string[]).includes(state.stage) ? TOP_UP_POLL_MS : false;
    },
  });
}

export function useComparison(searchId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.comparison(searchId ?? ''),
    queryFn: () => apiFetch<ComparisonState>(`${base(searchId ?? '')}/comparison`),
    enabled: enabled && searchId !== null && searchId !== '',
    retry: false,
  });
}

/**
 * Приём результата мутации четвёрки.
 *
 * Ответ мутации — **это и есть новое состояние** `GET /selection`: тот же
 * `SelectionOut`, посчитанный в той же транзакции. Он кладётся в кэш
 * напрямую, и перезапрашивать состав незачем: лишний round-trip только
 * добавляет шанс увидеть промежуточное состояние между ответом и refetch.
 *
 * Перезапрашиваются лишь те данные, которых в ответе нет:
 *
 * * `comparison` — производное представление, backend считает его отдельно;
 * * заказ и список заказов — стадия воронки меняется вместе с составом
 *   четвёрки, и трекер в шапке обязан это увидеть.
 *
 * Инвалидация **не ожидается** (`void`): она обновляет соседние экраны, а
 * кнопка на карточке не должна ждать чужих запросов, чтобы разблокироваться.
 */
function useApplySelection(searchId: string) {
  const queryClient = useQueryClient();
  return useCallback(
    (state: SelectionState) => {
      queryClient.setQueryData(queryKeys.selection(searchId), state);
      void queryClient.invalidateQueries({ queryKey: queryKeys.comparison(searchId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.searchSession(searchId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.searchSessions() });
    },
    [queryClient, searchId],
  );
}

export interface OutreachInput {
  /** Хэш редакции текста согласия из `GET /selection`. */
  textHash: string;
}

export function useRequestOutreach(searchId: string) {
  const apply = useApplySelection(searchId);

  return useMutation({
    mutationFn: ({ textHash }: OutreachInput) =>
      apiFetch<SelectionState>(`${base(searchId)}/selection/outreach`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        // Только согласие: состав вопросов и текст письма пользователь не
        // правит (решение заказчика от 09.09.2026). Нажатие закрывает состав.
        body: { consent: { text_hash: textHash, accepted: true } },
      }),
    onSuccess: apply,
  });
}

export interface RejectSlotInput {
  slot: number;
  /**
   * Автомобиль, который снимают.
   *
   * Обязателен: без него повтор запроса после успешной замены снял бы
   * **другой** автомобиль — тот, который только что занял слот.
   */
  vehicleId: string;
  reason?: RejectReason | undefined;
}

export function useRejectSlot(searchId: string) {
  const apply = useApplySelection(searchId);

  return useMutation({
    mutationFn: ({ slot, vehicleId, reason }: RejectSlotInput) =>
      apiFetch<SelectionState>(`${base(searchId)}/selection/${slot}/reject`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: { vehicle_id: vehicleId, reason: reason ?? null },
      }),
    onSuccess: apply,
  });
}

/**
 * «Отказаться от этого автомобиля» — после заморозки и **без замены** (§34.5).
 *
 * Не то же, что «Не подходит»: замены не будет, слот остаётся пустым, и
 * четвёрка становится тройкой. И не то же, что «Снять с торга»: то обратимо, а
 * это выводит автомобиль из заказа навсегда.
 */
export function useDeclineVehicle(searchId: string) {
  const apply = useApplySelection(searchId);

  return useMutation({
    mutationFn: (vehicleId: string) =>
      apiFetch<SelectionState>(`${base(searchId)}/selection/decline`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: { vehicle_id: vehicleId },
      }),
    onSuccess: apply,
  });
}

export interface SelectVehicleInput {
  vehicleId: string;
  /**
   * Мандат на торг.
   *
   * Уходит **тем же запросом**, что и выбор (§21): раздельные «выбрал» и
   * «выдал мандат» создавали бы состояние, в котором автомобиль выбран, а
   * границы торга не заданы, — и агент мог бы уйти в переписку без них.
   */
  mandate: MandateInput;
  /** Хэш редакции условий вознаграждения из `GET /selection`. */
  feeConsentHash: string;
}

export function useSelectVehicle(searchId: string) {
  const apply = useApplySelection(searchId);

  return useMutation({
    mutationFn: ({ vehicleId, mandate, feeConsentHash }: SelectVehicleInput) =>
      apiFetch<SelectionState>(`${base(searchId)}/select-vehicle`, {
        method: 'POST',
        idempotencyKey: randomUuid(),
        body: {
          vehicle_id: vehicleId,
          mandate,
          fee_consent: { text_hash: feeConsentHash, accepted: true },
        },
      }),
    onSuccess: apply,
  });
}
